import {
  Button,
  Card,
  CardBody,
  Icon,
  Skeleton,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/outline';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  useCreateFiscalAccountMutation,
  useDeleteFiscalAccountMutation,
  useGetFiscalAccountsQuery,
  useGetUserByIndividualIdentityIdQuery,
} from '@blockpulse3/graphql/hooks';
import { useErrorToast, useSuccessToast } from '@blockpulse3/ui/commons';

import { useUser } from '../../../providers';
import { FiscalAccountDeleteModal } from './FiscalAccountDeleteModal';
import { FiscalAccountEntry } from './FiscalAccountEntry';
import { FiscalAccountFormModal } from './FiscalAccountFormModal';
import { IFiscalAccountForm } from './types';

type Props = unknown;

export function FiscalAccountFormView(): JSX.Element {
  const i18nFiscalAccountForm = useTranslations('FiscalAccount');

  const deleteModalRef = useRef(null);
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { user: currentUser } = useUser();
  const { individualIdentityId = '' } = useParams();

  const { data: individualUser } = useGetUserByIndividualIdentityIdQuery({
    variables: {
      individualIdentityId,
    },
    skip: !individualIdentityId,
  });

  const user = individualIdentityId ? individualUser?.getUserByIndividualIdentityId : currentUser;

  /* ** Store fiscal account id to be deleted ** */
  const [fiscalAccountId, setFiscalAccountId] = useState<string | null>(null);

  const {
    data: fiscalAccountsReq,
    loading,
    refetch,
  } = useGetFiscalAccountsQuery({
    variables: { userId: user?.id || '' },
    skip: !user?.id,
  });
  const [createFiscalAccount] = useCreateFiscalAccountMutation();
  const [deleteFiscalAccount] = useDeleteFiscalAccountMutation();

  /* ** Modals ** */
  const formModal = useDisclosure();
  const deleteModal = useDisclosure();

  const handleConfirmModalOpen = (id: string): void => {
    setFiscalAccountId(id);
    deleteModal.onOpen();
  };

  const handleConfirmModalClose = (): void => {
    setFiscalAccountId(null);
    deleteModal.onClose();
  };

  const handleFiscalAccountCreate = (data: IFiscalAccountForm): void => {
    createFiscalAccount({
      variables: {
        createFiscalAccountInput: {
          userId: user?.id || '',
          accountNumber: data.accountNumber,
          type: data.type.value,
          bankName: data.bankName,
          bankCode: data.bankCode,
          bankEmail: data.bankEmail || null,
        },
      },
      onCompleted: () => {
        formModal.onClose();
        successToast({ title: i18nFiscalAccountForm('Toast.CreateSuccess') });
        refetch();
      },
      onError: () => {
        formModal.onClose();
        errorToast({ title: i18nFiscalAccountForm('Toast.CreateError') });
      },
    });
  };

  const handleFiscalAccountDelete = (): void => {
    if (fiscalAccountId) {
      deleteFiscalAccount({
        variables: {
          deleteFiscalAccountInput: {
            fiscalAccountId,
            userId: user?.id || '',
          },
        },
        onCompleted: () => {
          deleteModal.onClose();
          successToast({ title: i18nFiscalAccountForm('Toast.DeleteSuccess') });
          refetch();
        },
        onError: () => {
          errorToast({ title: i18nFiscalAccountForm('Toast.DeleteError') });
        },
      });
    }
  };

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: '5', lg: '8' }}>
      <Text flexBasis={{ lg: 250 }} fontSize="lg" fontWeight="medium">
        {i18nFiscalAccountForm('FormTitle')}
      </Text>
      <Card maxW={{ lg: '3xl' }} w="full">
        <CardBody>
          <Stack spacing="4">
            {loading ? (
              <Skeleton>
                <FiscalAccountEntry accountNumber="" bankName="" type="PEA" />
              </Skeleton>
            ) : (
              fiscalAccountsReq?.fiscalAccounts.map((fiscalAccount) => (
                <FiscalAccountEntry
                  key={fiscalAccount.id}
                  accountNumber={fiscalAccount.accountNumber}
                  bankName={fiscalAccount.bankName}
                  type={fiscalAccount.type}
                  onDelete={(): void => handleConfirmModalOpen(fiscalAccount.id)}
                />
              ))
            )}
            <Button variant="light" onClick={formModal.onOpen}>
              <Icon as={PlusIcon} mr="1" /> {i18nFiscalAccountForm('AddFiscalAccount')}
            </Button>
          </Stack>
        </CardBody>
      </Card>
      <FiscalAccountDeleteModal
        isOpen={deleteModal.isOpen}
        leastDestructiveRef={deleteModalRef}
        onClose={handleConfirmModalClose}
        onConfirm={handleFiscalAccountDelete}
      />
      <FiscalAccountFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        onSubmit={handleFiscalAccountCreate}
      />
    </Stack>
  );
}

export type FiscalAccountFormViewProps = Props;
